import React, { useEffect, useMemo } from "react";
import * as THREE from "three";

/**
 * Pipe = walec ustawiony wzdłuż odcinka A->B (oś walca wzdłuż Y).
 * Dla half-pipe robimy roll, żeby "wycięta" połowa była skierowana do góry (world up).
 *
 * openEnded:
 * - true  => brak dekli
 * - false => dekiel na obu końcach (dla half-pipe to pół-dysk)
 */
function Pipe({
                a,
                b,
                radius = 0.05,
                radialSegments = 18,
                material,
                thetaStart = 0,
                thetaLength = Math.PI * 2,
                offset = new THREE.Vector3(0, 0, 0),
                rollOpenToUp = false,
                openEnded = true,
                castShadow = true,
                receiveShadow = false,
              }) {
  const { position, quaternion, length } = useMemo(() => {
    const dir = new THREE.Vector3().subVectors(b, a);
    const baseLen = dir.length();
    const safeLen = Math.max(baseLen, 1e-6);

    const mid = new THREE.Vector3().addVectors(a, b).multiplyScalar(0.5).add(offset);

    // bazowa rotacja: local Y -> dir
    const baseQ = new THREE.Quaternion();
    const dirN = dir.clone().normalize();
    baseQ.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dirN);

    if (!rollOpenToUp) {
      return { position: mid, quaternion: baseQ, length: safeLen };
    }

    // CylinderGeometry używa: x = sin(theta), z = cos(theta)
    const full = Math.PI * 2;
    const missingLen = full - thetaLength;
    const missingMid = thetaStart + thetaLength + missingLen * 0.5;

    const localMissing = new THREE.Vector3(Math.sin(missingMid), 0, Math.cos(missingMid)).normalize();
    const currentMissing = localMissing.clone().applyQuaternion(baseQ).normalize();

    const worldUp = new THREE.Vector3(0, 1, 0);
    const upProj = worldUp.clone().sub(dirN.clone().multiplyScalar(worldUp.dot(dirN)));

    if (upProj.lengthSq() < 1e-8) upProj.set(0, 0, 1);
    else upProj.normalize();

    const cross = new THREE.Vector3().crossVectors(currentMissing, upProj);
    const sin = dirN.dot(cross);
    const cos = currentMissing.dot(upProj);
    const angle = Math.atan2(sin, cos);

    const rollQ = new THREE.Quaternion().setFromAxisAngle(dirN, angle);
    const finalQ = rollQ.multiply(baseQ);

    return { position: mid, quaternion: finalQ, length: safeLen };
  }, [a, b, offset, rollOpenToUp, thetaStart, thetaLength]);

  return (
    <mesh position={position} quaternion={quaternion} material={material} castShadow={castShadow} receiveShadow={receiveShadow}>
      <cylinderGeometry args={[radius, radius, length, radialSegments, 1, openEnded, thetaStart, thetaLength]} />
    </mesh>
  );
}

/**
 * ✅ SmoothElbow
 * Płynne kolanko: łuk (TubeGeometry) + opcjonalny prosty "tip" po 45°.
 */
function SmoothElbow({
                       start,
                       outward,
                       angleDeg = 45,
                       bendRadius = 0.08,
                       totalLength = 0.22,
                       tubeRadius = 0.035,
                       radialSegments = 10,
                       tubularSegments = 32,
                       materialTube,
                       materialTip,
                       groundY = 0,
                       endClearance = 0.03,
                       castShadow = true,
                       receiveShadow = false,
                     }) {
  const { curve, bendEnd, tipEnd, tipLen } = useMemo(() => {
    const P0 = start.clone();

    const down = new THREE.Vector3(0, -1, 0);
    const horiz = outward.clone().normalize();

    const angle = THREE.MathUtils.degToRad(angleDeg);

    const v1 = down
      .clone()
      .multiplyScalar(Math.cos(angle))
      .add(horiz.clone().multiplyScalar(Math.sin(angle)))
      .normalize();

    const v0 = down.clone();

    let k = new THREE.Vector3().crossVectors(v0, v1);
    if (k.lengthSq() < 1e-10) k = new THREE.Vector3(0, 0, 1);
    else k.normalize();

    const u0 = new THREE.Vector3().crossVectors(v0, k).normalize();
    const C = P0.clone().sub(u0.clone().multiplyScalar(bendRadius));

    const steps = Math.max(8, Math.floor(tubularSegments * 0.7));
    const points = [];
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const rot = new THREE.Quaternion().setFromAxisAngle(k, t * angle);
      const ui = u0.clone().applyQuaternion(rot);
      points.push(C.clone().add(ui.multiplyScalar(bendRadius)));
    }

    const PbendEnd = points[points.length - 1].clone();

    const bendArcLen = bendRadius * angle;
    let tip = Math.max(0, totalLength - bendArcLen);

    const minEndY = groundY + Math.max(0, endClearance);
    if (v1.y < -1e-6) {
      const maxTipByGround = (PbendEnd.y - minEndY) / (-v1.y);
      tip = Math.min(tip, Math.max(0, maxTipByGround));
    }

    const PtipEnd = PbendEnd.clone().addScaledVector(v1, tip);

    const crv = new THREE.CatmullRomCurve3(points, false, "centripetal");
    return { curve: crv, bendEnd: PbendEnd, tipEnd: PtipEnd, tipLen: tip };
  }, [start, outward, angleDeg, bendRadius, totalLength, tubularSegments, groundY, endClearance]);

  return (
    <group>
      <mesh material={materialTube} castShadow={castShadow} receiveShadow={receiveShadow}>
        <tubeGeometry args={[curve, tubularSegments, tubeRadius, radialSegments, false]} />
      </mesh>

      {tipLen > 1e-6 && (
        <Pipe
          a={bendEnd}
          b={tipEnd}
          radius={tubeRadius}
          radialSegments={radialSegments}
          material={materialTip}
          thetaStart={0}
          thetaLength={Math.PI * 2}
          offset={new THREE.Vector3(0, 0, 0)}
          rollOpenToUp={false}
          openEnded={true}
          castShadow={castShadow}
          receiveShadow={receiveShadow}
        />
      )}
    </group>
  );
}

/**
 * Klonuje teksturę i kopiuje podstawowe ustawienia filtrowania.
 * (ważne, bo repeat jest per-mesh i nie chcemy "walczyć" o jedną instancję tekstury)
 */
function cloneTex(src) {
  if (!src) return null;
  const t = src.clone?.() ?? null;
  if (!t) return null;

  t.wrapS = THREE.RepeatWrapping;
  t.wrapT = THREE.RepeatWrapping;

  // kopia filtrów / aniso (clone zwykle kopiuje, ale to bezpieczne)
  t.minFilter = src.minFilter;
  t.magFilter = src.magFilter;
  t.anisotropy = src.anisotropy ?? t.anisotropy;

  // zachowaj colorSpace/encoding jeśli istnieje
  if ("colorSpace" in src && "colorSpace" in t) t.colorSpace = src.colorSpace;
  if ("encoding" in src && "encoding" in t) t.encoding = src.encoding;

  t.needsUpdate = true;
  return t;
}

/**
 * Tworzy materiał plastiku z mapą base + normal.
 * repeatU/repeatV ustawiasz per element.
 */
function usePlasticMaterial({
                              hexa,
                              baseTex,
                              normalTex,
                              repeatU = 1,
                              repeatV = 1,
                              roughness = 0.65,
                              metalness = 0,
                              normalScale = 1.4,
                              doubleSide = true,
                            }) {
  const pack = useMemo(() => {
    const map = cloneTex(baseTex);
    if (map) map.repeat.set(Math.max(0.001, repeatU), Math.max(0.001, repeatV));

    const nrm = cloneTex(normalTex);
    if (nrm) nrm.repeat.set(Math.max(0.001, repeatU), Math.max(0.001, repeatV));

    const mat = new THREE.MeshStandardMaterial({
      color: new THREE.Color(hexa || "#d9d9d9"),
      metalness,
      roughness,
      side: doubleSide ? THREE.DoubleSide : THREE.FrontSide,
    });

    if (map) mat.map = map;
    if (nrm) {
      mat.normalMap = nrm;
      mat.normalScale = new THREE.Vector2(normalScale, normalScale);
    }

    mat.needsUpdate = true;
    return { mat, map, nrm };
  }, [hexa, baseTex, normalTex, repeatU, repeatV, roughness, metalness, normalScale, doubleSide]);

  useEffect(() => {
    return () => {
      pack.map?.dispose?.();
      pack.nrm?.dispose?.();
      pack.mat?.dispose?.();
    };
  }, [pack]);

  return pack.mat;
}

function PlasticPipe({
                       a,
                       b,
                       radius,
                       radialSegments,
                       thetaStart,
                       thetaLength,
                       offset,
                       rollOpenToUp,
                       openEnded,
                       castShadow,
                       receiveShadow,
                       hexa,
                       baseTex,
                       normalTex,
                       plasticTileSize = 0.25, // metry na tile (im mniejsze, tym więcej powtórzeń)
                       plasticRoughness = 0.65,
                       plasticNormalScale = 1.4,
                       doubleSide = true,
                     }) {
  // długość odcinka + długość łuku (żeby rozmiar wzoru był spójny)
  const { len, arcLen } = useMemo(() => {
    const dir = new THREE.Vector3().subVectors(b, a);
    const length = Math.max(1e-6, dir.length());
    const arc = Math.max(1e-6, radius * thetaLength); // łuk po obwodzie (dla half-pipe = r*pi)
    return { len: length, arcLen: arc };
  }, [a, b, radius, thetaLength]);

  // CylinderGeometry UV: U = dookoła, V = po długości
  const repeatU = arcLen / Math.max(1e-6, plasticTileSize);
  const repeatV = len / Math.max(1e-6, plasticTileSize);

  const mat = usePlasticMaterial({
    hexa,
    baseTex,
    normalTex,
    repeatU,
    repeatV,
    roughness: plasticRoughness,
    metalness: 0,
    normalScale: plasticNormalScale,
    doubleSide,
  });

  return (
    <Pipe
      a={a}
      b={b}
      radius={radius}
      radialSegments={radialSegments}
      material={mat}
      thetaStart={thetaStart}
      thetaLength={thetaLength}
      offset={offset}
      rollOpenToUp={rollOpenToUp}
      openEnded={openEnded}
      castShadow={castShadow}
      receiveShadow={receiveShadow}
    />
  );
}

function PlasticElbow({
                        start,
                        outward,
                        angleDeg,
                        bendRadius,
                        totalLength,
                        tubeRadius,
                        radialSegments,
                        tubularSegments,
                        groundY,
                        endClearance,
                        castShadow,
                        receiveShadow,
                        hexa,
                        baseTex,
                        normalTex,
                        plasticTileSize = 0.25,
                        plasticRoughness = 0.65,
                        plasticNormalScale = 1.4,
                      }) {
  // UV TubeGeometry: U = po długości krzywej, V = dookoła
  // Żeby było spójnie, liczymy repeat po długości łuku + obwód.
  const { curveLen, circ } = useMemo(() => {
    const down = new THREE.Vector3(0, -1, 0);
    const horiz = outward.clone().normalize();
    const angle = THREE.MathUtils.degToRad(angleDeg);

    const v1 = down
      .clone()
      .multiplyScalar(Math.cos(angle))
      .add(horiz.clone().multiplyScalar(Math.sin(angle)))
      .normalize();

    const v0 = down.clone();

    let k = new THREE.Vector3().crossVectors(v0, v1);
    if (k.lengthSq() < 1e-10) k = new THREE.Vector3(0, 0, 1);
    else k.normalize();

    const u0 = new THREE.Vector3().crossVectors(v0, k).normalize();
    const C = start.clone().sub(u0.clone().multiplyScalar(bendRadius));

    const steps = Math.max(8, Math.floor(tubularSegments * 0.7));
    const points = [];
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const rot = new THREE.Quaternion().setFromAxisAngle(k, t * angle);
      const ui = u0.clone().applyQuaternion(rot);
      points.push(C.clone().add(ui.multiplyScalar(bendRadius)));
    }

    const crv = new THREE.CatmullRomCurve3(points, false, "centripetal");
    const len = Math.max(1e-6, crv.getLength());
    const circumference = Math.max(1e-6, 2 * Math.PI * tubeRadius);

    return { curveLen: len, circ: circumference };
  }, [start, outward, angleDeg, bendRadius, tubularSegments, tubeRadius]);

  const repeatU = curveLen / Math.max(1e-6, plasticTileSize);
  const repeatV = circ / Math.max(1e-6, plasticTileSize);

  // osobny materiał dla tube (repeatU/repeatV)
  const matTube = usePlasticMaterial({
    hexa,
    baseTex,
    normalTex,
    repeatU,
    repeatV,
    roughness: plasticRoughness,
    metalness: 0,
    normalScale: plasticNormalScale,
    doubleSide: true, // rura pełna, nie trzeba double
  });

  // tip to zwykły walec: U dookoła, V po długości
  // tipLen jest zmienna – uproszczenie: przyjmij powtarzanie po długości ~ totalLength
  const tipRepeatU = (2 * Math.PI * tubeRadius) / Math.max(1e-6, plasticTileSize);
  const tipRepeatV = totalLength / Math.max(1e-6, plasticTileSize);

  const matTip = usePlasticMaterial({
    hexa,
    baseTex,
    normalTex,
    repeatU: tipRepeatU,
    repeatV: tipRepeatV,
    roughness: plasticRoughness,
    metalness: 0,
    normalScale: plasticNormalScale,
    doubleSide: true,
  });

  return (
    <SmoothElbow
      start={start}
      outward={outward}
      angleDeg={angleDeg}
      bendRadius={bendRadius}
      totalLength={totalLength}
      tubeRadius={tubeRadius}
      radialSegments={radialSegments}
      tubularSegments={tubularSegments}
      materialTube={matTube}
      materialTip={matTip}
      groundY={groundY}
      endClearance={endClearance}
      castShadow={castShadow}
      receiveShadow={receiveShadow}
    />
  );
}

export default function Gutters({
                                  toolingData,
                                  textures,
                                  gutterColor, // ✅ tylko to nas interesuje

                                  // geometria rynny
                                  gutterRadius = 0.045,
                                  gutterRadialSegments = 18,
                                  gutterOutset = -0.02,
                                  gutterDrop = 0.03,

                                  // korekty
                                  gutterLift = 0.01,
                                  gutterProtrude = 0.015,

                                  // rura spustowa
                                  downpipeRadius = 0.035,
                                  downpipeRadialSegments = 10,
                                  downpipeOutset = -0.02,
                                  downpipeInset = 0.12,
                                  downpipeTopDrop = 0.02,

                                  // smooth kolanko
                                  elbowEnabled = true,
                                  elbowLength = 0.22,
                                  elbowBendRadius = 0.08,
                                  elbowAngleDeg = 45,
                                  elbowTubularSegments = 32,

                                  // ziemia / wysokości
                                  groundY = 0,
                                  elbowStartClearance = 0.12,
                                  elbowEndClearance = 0.03,

                                  cornerSnapEps = 0.5,

                                  // logika wykluczania jak w Tooling
                                  enableDrainageCut = true,
                                  flatSideEps = 0.03,
                                  lowestEps = 0.03,
                                  disableRoofSides = null,

                                  // ✅ PLASTIC look
                                  plasticTileSize = 0.25,     // metry na jeden tile (zmniejsz => więcej detalu)
                                  plasticRoughness = 0.62,    // plastik
                                  plasticNormalScale = 1.6,   // siła normal mapy
                                }) {
  if (!toolingData) return null;

  // ✅ weź base + normal z Twojego loadera
  const plasticBase =
    textures?.plastic_default?.textureDiff ||
    textures?.plastic_default?.texture ||
    null;

  const plasticNormal =
    textures?.plastic_default?.textureNormalness ||
    textures?.plastic_default?.textureNormal ||
    textures?.plastic_default?.textureNormalMap ||
    textures?.plastic_default?.normalMap ||
    null;

  // kolor (fallback na walls_color)
  const hexa = useMemo(() => {
    const wallColor = textures?.walls_color;
    const src = gutterColor || wallColor || {};
    return src?.hex || src?.hexa || wallColor?.hex || "#d9d9d9";
  }, [gutterColor, textures?.walls_color]);

  const outwardBySide = useMemo(() => {
    return {
      minX: new THREE.Vector3(-1, 0, 0),
      maxX: new THREE.Vector3(1, 0, 0),
      minZ: new THREE.Vector3(0, 0, -1),
      maxZ: new THREE.Vector3(0, 0, 1),
    };
  }, []);

  const { gutterSegMeta } = useMemo(() => {
    const edges = toolingData?.roofEdges || [];
    if (!edges.length) return { gutterSegMeta: [] };

    const manualDisabled =
      Array.isArray(disableRoofSides) && disableRoofSides.length ? new Set(disableRoofSides) : null;

    const pts = [];
    for (const e of edges) {
      if (e?.a) pts.push(e.a);
      if (e?.b) pts.push(e.b);
    }
    if (pts.length < 2) return { gutterSegMeta: [] };

    const box = new THREE.Box3().setFromPoints(pts);
    const minX = box.min.x;
    const maxX = box.max.x;
    const minZ = box.min.z;
    const maxZ = box.max.z;

    const xSpan = Math.max(1e-6, maxX - minX);
    const zSpan = Math.max(1e-6, maxZ - minZ);
    const sideSnap = Math.max(0.03, Math.min(xSpan, zSpan) * 0.06);

    const segMeta = edges.map((seg) => {
      const ax = seg.a.x, bx = seg.b.x;
      const az = seg.a.z, bz = seg.b.z;

      const scoreMinX = Math.max(Math.abs(ax - minX), Math.abs(bx - minX));
      const scoreMaxX = Math.max(Math.abs(ax - maxX), Math.abs(bx - maxX));
      const scoreMinZ = Math.max(Math.abs(az - minZ), Math.abs(bz - minZ));
      const scoreMaxZ = Math.max(Math.abs(az - maxZ), Math.abs(bz - maxZ));

      let side = "minX";
      let best = scoreMinX;

      if (scoreMaxX < best) { best = scoreMaxX; side = "maxX"; }
      if (scoreMinZ < best) { best = scoreMinZ; side = "minZ"; }
      if (scoreMaxZ < best) { best = scoreMaxZ; side = "maxZ"; }

      const nearAny = best <= sideSnap;
      const y1 = seg.a.y;
      const y2 = seg.b.y;
      const avgY = (y1 + y2) * 0.5;

      return { seg, side, nearAny, y1, y2, avgY };
    });

    const toRemove = new Set();

    if (!enableDrainageCut) {
      if (manualDisabled) for (const s of manualDisabled) toRemove.add(s);
      return { gutterSegMeta: segMeta.filter((m) => m.nearAny && toRemove.has(m.side)) };
    }

    const sides = { minX: [], maxX: [], minZ: [], maxZ: [] };
    for (const m of segMeta) if (m.nearAny) sides[m.side].push(m);

    const sideStats = Object.entries(sides).map(([side, arr]) => {
      if (!arr.length) return { side, count: 0, meanY: Infinity, yRange: Infinity, isFlat: false };

      let sum = 0;
      let minY = Infinity;
      let maxY = -Infinity;

      for (const m of arr) {
        sum += m.avgY;
        minY = Math.min(minY, m.y1, m.y2);
        maxY = Math.max(maxY, m.y1, m.y2);
      }

      const meanY = sum / arr.length;
      const yRange = maxY - minY;
      const isFlat = yRange <= flatSideEps;

      return { side, count: arr.length, meanY, yRange, isFlat };
    });

    const statsBySide = sideStats.reduce((acc, s) => {
      acc[s.side] = s;
      return acc;
    }, {});

    const flatSides = sideStats.filter((s) => s.count > 0 && s.isFlat);

    if (flatSides.length) {
      let minMeanY = Infinity;
      for (const s of flatSides) minMeanY = Math.min(minMeanY, s.meanY);

      for (const s of flatSides) {
        if (s.meanY <= minMeanY + lowestEps) toRemove.add(s.side);
      }

      const opposite = { minX: "maxX", maxX: "minX", minZ: "maxZ", maxZ: "minZ" };
      for (const side of Array.from(toRemove)) {
        const opp = opposite[side];
        const a = statsBySide[side];
        const b = statsBySide[opp];
        if (a?.count > 0 && b?.count > 0 && a.isFlat && b.isFlat && Math.abs(a.meanY - b.meanY) <= lowestEps) {
          toRemove.add(opp);
        }
      }
    }

    if (manualDisabled) for (const s of manualDisabled) toRemove.add(s);

    return { gutterSegMeta: segMeta.filter((m) => m.nearAny && toRemove.has(m.side)) };
  }, [
    toolingData?.roofEdges,
    enableDrainageCut,
    flatSideEps,
    lowestEps,
    disableRoofSides,
  ]);

  /**
   * Downpipes:
   * - 1 downpipe na segment (krawędź)
   * - cofnięcie wzdłuż rynny o downpipeInset
   * - pionowo w dół do wysokości startu kolanka (min nad ziemią)
   */
  const downpipes = useMemo(() => {
    const corners = toolingData?.corners || [];
    if (!corners.length || !gutterSegMeta.length) return [];

    // ---- dynamiczny snap (skaluje się z rozmiarem dachu) ----
    const pts = [];
    for (const m of gutterSegMeta) {
      if (m?.seg?.a) pts.push(m.seg.a);
      if (m?.seg?.b) pts.push(m.seg.b);
    }

    let autoSnap = cornerSnapEps; // minimalnie tyle co było
    if (pts.length >= 2) {
      const box = new THREE.Box3().setFromPoints(pts);
      const spanX = Math.max(1e-6, box.max.x - box.min.x);
      const spanZ = Math.max(1e-6, box.max.z - box.min.z);
      const base = Math.min(spanX, spanZ);

      // 4% mniejszego wymiaru + clamp
      autoSnap = Math.max(cornerSnapEps, Math.min(6, Math.max(0.5, base * 0.04)));
    }
    const snap2 = autoSnap * autoSnap;

    const cornerTB = corners.map((seg, idx) => {
      const a = seg.a, b = seg.b;
      const top = a.y >= b.y ? a : b;
      const bottom = a.y < b.y ? a : b;
      return { idx, top, bottom };
    });

    // helper: znajdź najbliższy corner (po XZ)
    const findNearestCorner = (p) => {
      let best = null;
      let bestD2 = Infinity;

      for (const c of cornerTB) {
        const dx = c.top.x - p.x;
        const dz = c.top.z - p.z;
        const d2 = dx * dx + dz * dz;
        if (d2 < bestD2) {
          bestD2 = d2;
          best = c;
        }
      }
      return { best, bestD2 };
    };

    // 1) najpierw próbujemy normalnie, ale z autoSnap
    let endpointWithCorner = [];
    for (let edgeIndex = 0; edgeIndex < gutterSegMeta.length; edgeIndex++) {
      const m = gutterSegMeta[edgeIndex];
      const A = m.seg.a;
      const B = m.seg.b;

      const endpoints = [
        { p: A, other: B, side: m.side },
        { p: B, other: A, side: m.side },
      ];

      for (const ep of endpoints) {
        const { best, bestD2 } = findNearestCorner(ep.p);
        if (best && bestD2 <= snap2) {
          endpointWithCorner.push({
            edgeIndex,
            side: ep.side,
            p: ep.p,
            other: ep.other,
            corner: best,
            d2: bestD2,
          });
        }
      }
    }

    // 2) fallback: jeśli NIC nie weszło w próg, to nie tnij w ogóle – bierz nearest always
    if (endpointWithCorner.length === 0) {
      for (let edgeIndex = 0; edgeIndex < gutterSegMeta.length; edgeIndex++) {
        const m = gutterSegMeta[edgeIndex];
        const A = m.seg.a;
        const B = m.seg.b;

        const endpoints = [
          { p: A, other: B, side: m.side },
          { p: B, other: A, side: m.side },
        ];

        for (const ep of endpoints) {
          const { best, bestD2 } = findNearestCorner(ep.p);
          if (best) {
            endpointWithCorner.push({
              edgeIndex,
              side: ep.side,
              p: ep.p,
              other: ep.other,
              corner: best,
              d2: bestD2,
            });
          }
        }
      }
    }

    // tylko 1 downpipe na segment
    const bestPerEdge = new Map();
    for (const item of endpointWithCorner) {
      const prev = bestPerEdge.get(item.edgeIndex);
      if (!prev || item.d2 < prev.d2) bestPerEdge.set(item.edgeIndex, item);
    }

    const out = [];
    const angleRad = THREE.MathUtils.degToRad(elbowAngleDeg);
    const bendArcLen = elbowBendRadius * angleRad;
    const tipLen0 = Math.max(0, elbowLength - bendArcLen);
    const dropBend = elbowBendRadius * Math.sin(angleRad);
    const dropTip = tipLen0 * Math.cos(angleRad);

    for (const item of bestPerEdge.values()) {
      const outVec = outwardBySide[item.side] || new THREE.Vector3(0, 0, 0);

      const along = new THREE.Vector3().subVectors(item.other, item.p);
      const len = along.length();
      if (len < 1e-6) continue;

      along.normalize();
      const insetClamped = Math.max(0, Math.min(downpipeInset, len * 0.45));
      const pIn = item.p.clone().add(along.multiplyScalar(insetClamped));

      const yOff = -gutterDrop + gutterLift;
      const outDistPipe = downpipeOutset + gutterProtrude;

      const start = pIn
        .clone()
        .add(outVec.clone().multiplyScalar(outDistPipe))
        .add(new THREE.Vector3(0, yOff - downpipeTopDrop, 0));

      const minEndYByClear = groundY + Math.max(0, elbowStartClearance);
      const minEndYByOutlet = groundY + Math.max(0, elbowEndClearance) + dropBend + dropTip;
      const minEndY = Math.max(minEndYByClear, minEndYByOutlet);

      const end = start.clone();
      end.y = Math.max(item.corner.bottom.y, minEndY);

      if (start.y <= end.y + 1e-3) continue;

      out.push({ a: start, b: end, side: item.side });
    }

    return out;
  }, [
    toolingData?.corners,
    gutterSegMeta,
    cornerSnapEps,
    outwardBySide,
    gutterDrop,
    gutterLift,
    gutterProtrude,
    downpipeOutset,
    downpipeInset,
    downpipeTopDrop,
    groundY,
    elbowStartClearance,
    elbowEndClearance,
    elbowLength,
    elbowBendRadius,
    elbowAngleDeg,
  ]);

  return (
    <group>
      {/* Rynny + ścianki */}
      {gutterSegMeta.map((m, i) => {
        const outVec = outwardBySide[m.side] || new THREE.Vector3(0, 0, 0);

        const outDist = gutterOutset + gutterProtrude;
        const yOff = -gutterDrop + gutterLift;

        const offset = outVec.clone().multiplyScalar(outDist).add(new THREE.Vector3(0, yOff, 0));

        return (
          <PlasticPipe
            key={`gutter-${i}`}
            a={m.seg.a}
            b={m.seg.b}
            radius={gutterRadius}
            radialSegments={gutterRadialSegments}
            thetaStart={0}
            thetaLength={Math.PI} // pół-okrąg
            offset={offset}
            rollOpenToUp={true}
            openEnded={false}
            castShadow={true}
            receiveShadow={false}
            hexa={hexa}
            baseTex={plasticBase}
            normalTex={plasticNormal}
            plasticTileSize={plasticTileSize}
            plasticRoughness={plasticRoughness}
            plasticNormalScale={plasticNormalScale}
            doubleSide={true}
          />
        );
      })}

      {/* Downpipes */}
      {downpipes.map((seg, i) => (
        <PlasticPipe
          key={`downpipe-${i}`}
          a={seg.a}
          b={seg.b}
          radius={downpipeRadius}
          radialSegments={downpipeRadialSegments}
          thetaStart={0}
          thetaLength={Math.PI * 2}
          offset={new THREE.Vector3(0, 0, 0)}
          rollOpenToUp={false}
          openEnded={true}
          castShadow={true}
          receiveShadow={false}
          hexa={hexa}
          baseTex={plasticBase}
          normalTex={plasticNormal}
          plasticTileSize={plasticTileSize}
          plasticRoughness={plasticRoughness}
          plasticNormalScale={plasticNormalScale}
          doubleSide={false}
        />
      ))}

      {/* Kolanka */}
      {elbowEnabled &&
        downpipes.map((seg, i) => (
          <PlasticElbow
            key={`elbow-${i}`}
            start={seg.b}
            outward={(outwardBySide[seg.side] || new THREE.Vector3(0, 0, 0)).clone().normalize()}
            angleDeg={elbowAngleDeg}
            bendRadius={elbowBendRadius}
            totalLength={elbowLength}
            tubeRadius={downpipeRadius}
            radialSegments={downpipeRadialSegments}
            tubularSegments={elbowTubularSegments}
            hexa={hexa}
            baseTex={plasticBase}
            normalTex={plasticNormal}
            plasticTileSize={plasticTileSize}
            plasticRoughness={plasticRoughness}
            plasticNormalScale={plasticNormalScale}
            groundY={groundY}
            endClearance={elbowEndClearance}
            castShadow={true}
            receiveShadow={false}
          />
        ))}
    </group>
  );
}
